import { Component } from '@theme/component';

const STORAGE_KEY = 'battery-calculator-data';

/**
 * @typedef {{
 *   steps: Element[],
 *   form: HTMLFormElement,
 *   rows: Element[],
 *   loadTable: HTMLTableElement,
 *   totalKwh: HTMLElement,
 *   resultsKwh: HTMLElement,
 *   resultsBatteryKwh: HTMLElement,
 *   resultsBatteryAh: HTMLElement,
 *   bank100: HTMLElement,
 *   bank270: HTMLElement,
 *   productsTable: HTMLTableElement,
 *   productCard: Element[],
 * }} CalculatorRefs
 */

/** @extends {Component<CalculatorRefs>} */
class BatteryCalculator extends Component {
  /** @type {number} */
  #activeStepIndex = 0;

  /** @type {boolean} */
  #contactInfoCompleted = false;

  /** @type {number} */
  #customRowCount = 0;

  /** @type {((event: PopStateEvent) => void) | null} */
  #popstateHandler = null;

  /** @type {number} */
  #recommendedKwh = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#loadFromStorage();
    this.#ensureRadioDefaults();

    // load the first valid step from 0 to the desired step from url
    const urlStepIndex = this.#getStepFromUrl();
    const invalidStepIndex = this.#getInvalidStepIndex(Number(urlStepIndex));
    if (urlStepIndex !== null) {
      this.#activeStepIndex = invalidStepIndex > -1 ? Math.max(0, invalidStepIndex) : urlStepIndex;
    }

    this.#showStep(this.#activeStepIndex);

    // If we landed directly on the results step (URL deep-link, hard reload,
    // or restored state), re-run calculations + product filtering. Without
    // this, all product cards default to visible because the filter only
    // fires from `change` events or `handleNextStep`.
    const steps = this.refs.steps;
    const currentStep = Array.isArray(steps) ? steps[this.#activeStepIndex] : null;
    if (currentStep instanceof HTMLElement && currentStep.dataset.step === 'results') {
      this.#calculateTotalKwh();
      this.#filterProductRecommendations();
    }

    // Set up popstate listener for browser back/forward navigation
    this.#popstateHandler = () => {
      const stepFromUrl = this.#getStepFromUrl();
      if (stepFromUrl !== null) {
        this.#activeStepIndex = stepFromUrl;
        this.#showStep(this.#activeStepIndex);
        this.#saveToStorage();
      }
    };
    window.addEventListener('popstate', this.#popstateHandler);

    // Listen for changes on recommendation fields
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-affects-recommendations]')) {
        this.#filterProductRecommendations();
      }
    });
  }

  /**
   * For each radio group (inputs sharing a `name`), if none is checked after
   * localStorage restore, check the first one in the group. This gives every
   * question a default answer when the user has no stored value for it.
   */
  #ensureRadioDefaults() {
    const form = this.refs.form;
    if (!form) return;

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (form.querySelectorAll('input[type="radio"]'));

    /** @type {Map<string, HTMLInputElement[]>} */
    const groups = new Map();
    for (const radio of radios) {
      if (!radio.name) continue;
      const group = groups.get(radio.name);
      if (group) {
        group.push(radio);
      } else {
        groups.set(radio.name, [radio]);
      }
    }

    for (const group of groups.values()) {
      const hasChecked = group.some((r) => r.checked);
      if (!hasChecked && group[0]) {
        group[0].checked = true;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#popstateHandler) {
      window.removeEventListener('popstate', this.#popstateHandler);
      this.#popstateHandler = null;
    }
  }

  /**
   * Get the step index from the URL query parameter by matching
   * against step names configured via data-step attributes.
   *
   * @returns {number | null}
   */
  #getStepFromUrl() {
    const steps = this.refs.steps;
    if (!Array.isArray(steps)) return null;

    const params = new URLSearchParams(window.location.search);
    const stepParam = params.get('step');
    if (stepParam === null) return null;

    const index = steps.findIndex((step) => step instanceof HTMLElement && step.dataset.step === stepParam);
    return index >= 0 ? index : null;
  }

  /**
   * Get the first invalid step from 0 to the supplied index
   * @param {number} index
   * @returns {number}
   */
  #getInvalidStepIndex(index) {
    return this.refs.steps.slice(0, index).findIndex((step) => {
      return [...step.querySelectorAll('input:required:invalid')].length > 0;
    });
  }

  // ---------------------------------------------------------------------------
  // Step navigation
  // ---------------------------------------------------------------------------

  handleNextStep() {
    const steps = this.refs.steps;
    if (!Array.isArray(steps)) return;

    const currentStep = steps[this.#activeStepIndex];
    if (!currentStep) return;

    // Validate required fields in the current step
    const invalidField = currentStep.querySelector(':invalid');
    if (invalidField instanceof HTMLElement) {
      /** @type {HTMLInputElement} */ (invalidField).reportValidity();
      return;
    }

    // Mark contact info as completed after the first step
    if (this.#activeStepIndex === 0) {
      this.#contactInfoCompleted = true;
    }

    // Persist data after contact info step is completed
    if (this.#contactInfoCompleted) {
      this.#saveToStorage();
    }

    // Advance to the next step (includes the results step at the end)
    if (this.#activeStepIndex < steps.length - 1) {
      this.#activeStepIndex++;
      this.#showStep(this.#activeStepIndex);
      this.#saveStepToUrl('push');

      // If advancing to results step, filter products and ensure calculations are fresh
      const nextStep = steps[this.#activeStepIndex];
      if (nextStep?.dataset.step === 'results') {
        this.#calculateTotalKwh();
        this.#filterProductRecommendations();
      }

      // Fire Klaviyo integration when the user reaches the results step
      if (this.#activeStepIndex === steps.length - 1 && !this.#resultsReached) {
        this.#resultsReached = true;
        this.#onReachResults();
      }
    }
  }

  /** @type {boolean} */
  #resultsReached = false;

  /**
   * Show the step at the given index and hide all others.
   * CSS rules generated in battery-calculator.liquid toggle visibility based
   * on the data-active-step attribute — JS only needs to update it.
   * Also updates the URL with the step name query parameter.
   *
   * When advancing into the results step, also set `data-on-results` so the
   * results section (rendered outside the outer <form>) becomes visible while
   * the form is hidden. Results lives outside the form so the product cards'
   * nested add-to-cart <form> elements aren't swallowed by HTML's no-nested-
   * forms rule.
   *
   * @param {number} index
   */
  #showStep(index) {
    this.dataset.activeStep = String(index + 1);

    const steps = this.refs.steps;
    const isResults = Array.isArray(steps) && index === steps.length - 1;
    if (isResults) {
      this.dataset.onResults = '';
    } else {
      delete this.dataset.onResults;
    }

    this.#saveStepToUrl();

    // Scroll to the top of the <battery-calculator> host element on every
    // step change. The host element is always the first element of the
    // calculator container, so this gives a consistent "start at the top"
    // experience regardless of which step is shown. Run on next frame so
    // the display:none/display:block swap has finished laying out before
    // we measure.
    requestAnimationFrame(() => {
      this.scrollIntoView({ behavior: 'instant', block: 'start' });
    });
  }

  #saveStepToUrl(type = 'replace') {
    const steps = this.refs.steps;
    const stepEl = Array.isArray(steps) ? steps[this.#activeStepIndex] : null;
    const stepName = stepEl instanceof HTMLElement ? stepEl.dataset.step : null;

    const url = new URL(window.location.href);
    if (stepName) {
      url.searchParams.set('step', stepName);
    }
    if (type === 'replace') {
      history.replaceState(null, '', url);
    } else {
      history.pushState(null, '', url);
    }
  }

  // ---------------------------------------------------------------------------
  // Form submission (prevents default, handled by step navigation)
  // ---------------------------------------------------------------------------

  /**
   * @param {SubmitEvent} event
   */
  submit(event) {
    event.preventDefault();
  }

  // ---------------------------------------------------------------------------
  // Product recommendation filtering
  // ---------------------------------------------------------------------------

  /**
   * Filter product recommendations based on configurable fields.
   * Finds all inputs with data-affects-recommendations attribute and builds
   * a calculator tag from their values. Products matching the tag are shown.
   */
  #filterProductRecommendations() {
    const productCards = this.refs.productCard;
    if (!Array.isArray(productCards) || productCards.length === 0) return;

    // Find all inputs that affect recommendations
    const inputs = this.querySelectorAll('input[data-affects-recommendations]:checked');

    // Build tag parts from field values
    const values = [];

    inputs.forEach((input) => {
      values.push(input.value);
    });

    // Build calculator tag (use DOM order as specified)
    const calculatorTag = values.length > 0 ? `calculator:${values.join('-')}` : null;
    productCards.forEach((card) => {
      const cardTag = card.dataset.calculatorTag;
      card.classList.toggle('tw:hidden', cardTag !== calculatorTag);
    });

    // Update quantities based on recommended Ah and each product's battery capacity.
    this.#updateProductQuantities();
  }

  /**
   * Calculate and set the recommended quantity for each product card.
   * qty = ceil(recommendedAh / product's batteryAh), fallback to 1.
   * Replaces the quantity selector with a static "Qty: X" label.
   * The quantity selector wrapper is hidden via CSS; JS only injects the label.
   */
  #updateProductQuantities() {
    const productCards = this.refs.productCard;
    if (!Array.isArray(productCards) || productCards.length === 0) return;

    const recommendedKwh = this.#recommendedKwh;
    const voltage = this.#getSystemVoltage();
    const multiple = voltage / 12;

    productCards.forEach((card) => {
      const batteryAh = Number(card.dataset.batteryAh) || 0;
      const capacity = batteryAh === 100 ? 1.2 : 3.25;
      const qty = batteryAh > 0 && recommendedKwh > 0 ? Math.ceil(recommendedKwh / capacity / multiple) * multiple : 1;

      card.dataset.quantity = String(qty);

      // Sync to the hidden quantity input inside the nested product form.
      const qtyInput = card.querySelector('input[name="quantity"]');
      if (qtyInput) {
        qtyInput.value = String(qty);
        qtyInput.dataset.cartQuantity = String(qty);
      }

      // Inject "Qty: X" label before the hidden quantity selector.
      const qtyWrapper = card.querySelector('.quantity-selector-wrapper');
      if (qtyWrapper) {
        let label = card.querySelector('.calculator__qty-label');
        if (!label) {
          label = document.createElement('span');
          label.className = 'calculator__qty-label';
          qtyWrapper.parentNode.insertBefore(label, qtyWrapper);
        }
        label.textContent = `Qty: ${qty}`;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Table row interactions
  // ---------------------------------------------------------------------------

  /**
   * Toggle a table row on/off. When toggled on the row's inputs become active
   * and editable; when toggled off they are disabled and show "Not selected".
   *
   * @param {Event} event
   */
  handleRowToggle(event) {
    const checkbox = /** @type {HTMLInputElement} */ (event.target);
    const row = checkbox.closest('tr');
    if (!row) return;

    const isSelected = checkbox.checked;
    const inputs = row.querySelectorAll('[data-row-input]');
    const placeholders = row.querySelectorAll('[data-row-placeholder]');

    inputs.forEach((input) => {
      const el = /** @type {HTMLInputElement | HTMLSelectElement} */ (input);
      // Check if this is a locked hours input
      const isLocked = el.hasAttribute('data-locked');

      // Locked hours inputs stay disabled even when selected
      if (isLocked) {
        el.disabled = true;
      } else {
        el.disabled = !isSelected;
      }

      /** @type {HTMLElement} */ (input).classList.toggle('tw:hidden', !isSelected);
    });

    placeholders.forEach((el) => {
      /** @type {HTMLElement} */ (el).classList.toggle('tw:hidden', isSelected);
    });

    if (isSelected) {
      this.#calculateRowWh(row);
    } else {
      const whCell = row.querySelector('[data-row-wh]');
      if (whCell) {
        const placeholder = whCell.querySelector('[data-row-placeholder]');
        if (placeholder) {
          /** @type {HTMLElement} */ (placeholder).classList.remove('tw:hidden');
        }
        const valueSpan = whCell.querySelector('[data-row-wh-value]');
        if (valueSpan) valueSpan.remove();
      }
    }

    this.#calculateTotalKwh();
    this.#saveIfReady();
  }

  /**
   * Recalculate a row's Wh when any of its inputs change.
   *
   * @param {Event} event
   */
  handleRowInput(event) {
    const row = /** @type {HTMLElement} */ (event.target).closest('tr');
    if (!row) return;

    this.#calculateRowWh(row);
    this.#calculateTotalKwh();
    this.#saveIfReady();
  }

  /**
   * Handle option select change - update wattage from selected option's data attribute.
   *
   * @param {Event} event
   */
  handleRowOptionChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const row = select.closest('tr');
    if (!row) return;

    const selectedOption = select.options[select.selectedIndex];
    const wattage = selectedOption?.dataset?.wattage;

    if (wattage) {
      const wattageInput = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_wattage"]'));
      if (wattageInput) {
        wattageInput.value = wattage;
      }
    }

    this.#calculateRowWh(row);
    this.#calculateTotalKwh();
    this.#saveIfReady();
  }

  /**
   * Calculate Wh for a single row: quantity × wattage × hours.
   *
   * @param {Element} row
   */
  #calculateRowWh(row) {
    const qtyEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_qty"]'));
    const wattEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_wattage"]'));
    const hoursEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_hours"]'));

    const qty = Number(qtyEl?.value) || 0;
    const wattage = Number(wattEl?.value) || 0;
    const hours = Number(hoursEl?.value) || 0;

    const wh = qty * wattage * hours;
    const whCell = row.querySelector('[data-row-wh]');

    if (whCell) {
      const placeholder = whCell.querySelector('[data-row-placeholder]');
      if (placeholder) {
        /** @type {HTMLElement} */ (placeholder).classList.add('tw:hidden');
      }

      let valueSpan = whCell.querySelector('[data-row-wh-value]');
      if (!valueSpan) {
        valueSpan = document.createElement('span');
        valueSpan.setAttribute('data-row-wh-value', '');
        whCell.appendChild(valueSpan);
      }
      valueSpan.textContent = String(wh);
    }
  }

  /**
   * Sum all active row Wh values and update the total display.
   * Also calculates recommended battery requirements.
   */
  #calculateTotalKwh() {
    const rows = this.refs.rows;
    if (!Array.isArray(rows)) return;

    let totalWh = 0;

    for (const row of rows) {
      const toggle = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-row-toggle]'));
      if (!toggle?.checked) continue;

      const qtyEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_qty"]'));
      const wattEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_wattage"]'));
      const hoursEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_hours"]'));

      const qty = Number(qtyEl?.value) || 0;
      const wattage = Number(wattEl?.value) || 0;
      const hours = Number(hoursEl?.value) || 0;

      totalWh += qty * wattage * hours;
    }

    const totalKwh = totalWh / 1000;
    const totalKwhFormatted = totalKwh.toFixed(2);

    // Update total kWh displays
    if (this.refs.totalKwh) {
      this.refs.totalKwh.textContent = totalKwhFormatted;
    }

    if (this.refs.resultsKwh) {
      this.refs.resultsKwh.textContent = totalKwhFormatted;
    }

    // Calculate recommended battery requirements
    this.#calculateRecommendations(totalKwh);
  }

  /**
   * Calculate battery recommendations based on total kWh.
   *
   * @param {number} totalKwh
   */
  #calculateRecommendations(totalKwh) {
    // Get safety margin from table data attribute (default 1.18)
    const loadTable = this.refs.loadTable;
    const safetyMargin = loadTable ? Number(loadTable.dataset.safetyMargin) || 0.85 : 0.85;

    // Get system voltage from radio buttons
    const voltage = this.#getSystemVoltage();

    // Recommended Battery kWh = Total kWh / safety margin
    const recommendedKwh = totalKwh / safetyMargin;
    this.#recommendedKwh = recommendedKwh;

    // Update results display
    if (this.refs.resultsBatteryKwh) {
      this.refs.resultsBatteryKwh.textContent = recommendedKwh > 0 ? recommendedKwh.toFixed(2) : '—';
    }

    // Recommended Battery Ah = (Recommended kWh × 1000) ÷ voltage
    const recommendedAh = voltage > 0 ? (recommendedKwh / voltage) * 1000 : 0;
    if (this.refs.resultsBatteryAh) {
      this.refs.resultsBatteryAh.textContent = recommendedAh > 0 ? `${Math.ceil(recommendedAh)} Ah @ ${voltage}V` : '—';
    }
  }

  /**
   * Get the selected system voltage from radio buttons.
   * Defaults to 12V if none selected.
   *
   * @returns {number}
   */
  #getSystemVoltage() {
    const voltageInput = this.querySelector('input[name="voltage"]:checked');
    if (!voltageInput) return 12;

    const value = voltageInput.value;
    // Handle values like "12v", "24v", "48v" or "12", "24", "48"
    const match = value.match(/^(\d+)/);
    return match ? Number(match[1]) : 12;
  }

  // ---------------------------------------------------------------------------
  // Add custom row
  // ---------------------------------------------------------------------------

  addCustomRow() {
    this.#customRowCount++;
    const name = `custom_item_${this.#customRowCount}`;
    const table = this.refs.loadTable;
    if (!table) return;

    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const row = document.createElement('tr');
    row.setAttribute('ref', 'rows[]');
    row.className = 'tw:border-b tw:border-lightest-gray';
    row.dataset.rowId = name;
    row.dataset.rowName = `Custom Item ${this.#customRowCount}`;
    row.dataset.rowMinHours = '0';
    row.dataset.rowMaxHours = '24';

    row.innerHTML = `
      <td class="tw:py-3 tw:pr-4">
        <div class="tw:flex tw:items-center tw:gap-3">
          <label class="tw:flex tw:items-center tw:gap-2 tw:cursor-pointer">
            <input
              type="checkbox"
              on:change="/handleRowToggle"
              class="tw:w-5 tw:h-5 tw:accent-blue tw:cursor-pointer"
              data-row-toggle
              checked
            >
            <input
              type="text"
              name="${name}_title"
              placeholder="Custom Item ${this.#customRowCount}"
              class="tw:border tw:border-light-gray tw:rounded tw:p-1 tw:w-32"
            >
          </label>
        </div>
      </td>
      <td class="tw:py-3 tw:px-2 tw:text-center">
        <input type="number" name="${name}_qty" min="1" value="1" on:input="/handleRowInput"
          class="tw:w-16 tw:text-center tw:border tw:border-light-gray tw:rounded tw:p-1" data-row-input>
      </td>
      <td class="tw:py-3 tw:px-2 tw:text-center">
        <span class="tw:text-medium-gray">&mdash;</span>
      </td>
      <td class="tw:py-3 tw:px-2 tw:text-center">
        <input type="number" name="${name}_wattage" min="0" value="0" on:input="/handleRowInput"
          class="tw:w-20 tw:text-center tw:border tw:border-light-gray tw:rounded tw:p-1" data-row-input>
      </td>
      <td class="tw:py-3 tw:px-2 tw:text-center">
        <input type="number" name="${name}_hours" min="0" max="24" value="0" on:input="/handleRowInput"
          class="tw:w-16 tw:text-center tw:border tw:border-light-gray tw:rounded tw:p-1" data-row-input>
      </td>
      <td class="tw:py-3 tw:pl-2 tw:text-center tw:font-semibold" aria-live="polite" data-row-wh>
        <span data-row-wh-value>0</span>
      </td>
    `;

    tbody.appendChild(row);
  }

  // ---------------------------------------------------------------------------
  // Add to Cart (placeholder — real logic added later)
  // ---------------------------------------------------------------------------

  /**
   * @param {Event} event
   */
  addToCart(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const variantId = button.dataset.productAtc;
    if (!variantId) return;

    // TODO: Integrate with Shopify cart API
    console.log('Add to cart:', variantId);
  }

  // ---------------------------------------------------------------------------
  // Klaviyo integration
  //
  // On reaching the results step:
  //   1. Fire a "Submitted Battery Calculator" event via Klaviyo's Create
  //      Client Event endpoint.
  //   2. If the user opted in, subscribe them via Create Client Subscription.
  //
  // Both calls use Klaviyo's Client API — public company_id only, no secret.
  // ---------------------------------------------------------------------------

  #KLAVIYO_API_REVISION = '2026-04-15';

  async #onReachResults() {
    const config = this.#getKlaviyoConfig();
    const contact = this.#getContactInfo();

    if (!config?.companyId) {
      console.warn('[battery-calculator] No Klaviyo company_id configured; skipping integration.');
      return;
    }

    if (!contact.email) {
      console.warn('[battery-calculator] No email captured; skipping Klaviyo integration.');
      return;
    }

    const properties = this.#buildKlaviyoEventProperties();

    const tasks = [this.#sendKlaviyoEvent(contact, properties, config.companyId)];

    if (this.#isOptedIn() && config.listId) {
      tasks.push(this.#subscribeToKlaviyoList(contact.email, config.listId, config.companyId));
    }

    await Promise.allSettled(tasks);
  }

  /**
   * @returns {{ listId: string, companyId: string } | null}
   */
  #getKlaviyoConfig() {
    const optInEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-klaviyo-opt-in]'));
    if (!optInEl) return null;
    return {
      listId: optInEl.dataset.klaviyoListId ?? '',
      companyId: optInEl.dataset.klaviyoCompanyId ?? '',
    };
  }

  /**
   * Pull contact info from the form. Email is required for Klaviyo identify;
   * name and phone are optional but useful for profile enrichment.
   *
   * @returns {{ email: string | null, firstName: string | null, lastName: string | null, phone: string | null }}
   */
  #getContactInfo() {
    const form = this.refs.form;
    if (!form) {
      return { email: null, firstName: null, lastName: null, phone: null };
    }

    const emailInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="email"]'));
    const phoneInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[type="tel"], input[name="phone"]')
    );
    const nameInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="name"]'));

    const email = emailInput?.value?.trim() ?? null;
    const phone = phoneInput?.value?.trim() ?? null;
    const fullName = nameInput?.value?.trim() ?? '';

    /** @type {string | null} */
    let firstName = null;
    /** @type {string | null} */
    let lastName = null;
    if (fullName) {
      const parts = fullName.split(/\s+/);
      firstName = parts[0] ?? null;
      if (parts.length > 1) lastName = parts.slice(1).join(' ');
    }

    return { email, firstName, lastName, phone };
  }

  /** @returns {boolean} */
  #isOptedIn() {
    const checkbox = /** @type {HTMLInputElement | undefined} */ (this.refs.klaviyoOptInCheckbox);
    return Boolean(checkbox?.checked);
  }

  /**
   * @returns {Record<string, unknown>}
   */
  #buildKlaviyoEventProperties() {
    return {
      system: this.#buildSystem(),
      calculations: this.#buildCalculations(),
      recommendedProducts: this.#buildRecommendedProducts(),
      /** answers: this.#collectRawAnswers(), { this answers object contains all calculator answers and can be used in future } */
    };
  }

  /**
   * Raw form field name → value map. Useful for Klaviyo flow conditions
   * where machine-readable keys are easier to target than display labels.
   *
   * @returns {Record<string, string>}
   */
  // #collectRawAnswers() {
  //   const form = this.refs.form;
  //   if (!form) return {};

  //   /** @type {Record<string, string>} */
  //   const raw = {};
  //   const formData = new FormData(form);
  //   for (const [key, value] of formData.entries()) {
  //     if (typeof value === 'string') {
  //       raw[key] = value;
  //     }
  //   }
  //   return raw;
  // }

  /**
   * Look up a form answer by trying a list of candidate FormData names.
   * Used to resolve semantic fields (heated, voltage, bluetoothMonitor) whose
   * input names are configured by the admin in the theme editor.
   *
   * @param {string[]} candidates
   * @returns {string | null}
   */
  #readAnswer(candidates) {
    const form = this.refs.form;
    if (!form) return null;
    const data = new FormData(form);
    for (const name of candidates) {
      const value = data.get(name);
      if (typeof value === 'string' && value.length > 0) return value;
    }
    return null;
  }

  /**
   * @returns {{ heated: boolean, items: Array<Record<string, unknown>>, voltage: number, bluetoothMonitor: string }}
   */
  #buildSystem() {
    const heatedRaw = (this.#readAnswer(['heated', 'battery_type', 'cold_temps']) || '').toLowerCase();
    const heated = heatedRaw === 'heated';

    const voltageRaw = this.#readAnswer(['voltage', 'system_voltage']) || '';
    const voltage = parseInt(String(voltageRaw).replace(/[^0-9]/g, ''), 10) || 0;

    const bluetoothMonitor = this.#readAnswer(['bluetooth_monitor', 'monitor', 'battery_monitor']) || 'standard';

    return {
      heated,
      items: this.#buildSystemItems(),
      voltage,
      bluetoothMonitor,
    };
  }

  /**
   * @returns {Array<Record<string, unknown>>}
   */
  #buildSystemItems() {
    const rows = this.refs.rows;
    if (!Array.isArray(rows)) return [];

    /** @type {Array<Record<string, unknown>>} */
    const items = [];
    for (const row of rows) {
      const el = /** @type {HTMLElement} */ (row);
      const toggle = /** @type {HTMLInputElement | null} */ (el.querySelector('[data-row-toggle]'));
      const qtyEl = /** @type {HTMLInputElement | null} */ (el.querySelector('[name$="_qty"]'));
      const wattEl = /** @type {HTMLInputElement | null} */ (el.querySelector('[name$="_wattage"]'));
      const hoursEl = /** @type {HTMLInputElement | null} */ (el.querySelector('[name$="_hours"]'));

      const enabled = Boolean(toggle?.checked);
      if (!enabled) continue;

      const qty = Number(qtyEl?.value) || 0;
      const wattage = Number(wattEl?.value) || 0;
      const runHours = Number(hoursEl?.value) || 0;
      const wh = qty * wattage * runHours;

      const minHoursAttr = Number(el.dataset.rowMinHours);
      const maxHoursAttr = Number(el.dataset.rowMaxHours);

      items.push({
        wh,
        id: el.dataset.rowId || '',
        enabled,
        qty,
        minRunHours: Number.isFinite(minHoursAttr) ? minHoursAttr : runHours,
        maxRunHours: Number.isFinite(maxHoursAttr) ? maxHoursAttr : runHours,
        runHours,
        wattage,
        name: el.dataset.rowName || '',
      });
    }
    return items;
  }

  /**
   * @returns {Record<string, number>}
   */
  #buildCalculations() {
    const totalKWh = Number(this.refs.resultsKwh?.textContent) || 0;
    const loadTable = this.refs.loadTable;
    const safetyMargin = loadTable ? Number(loadTable.dataset.safetyMargin) || 1.18 : 1.18;
    const voltage = this.#getSystemVoltage();
    const recommendedBatteryKWh = totalKWh * safetyMargin;
    const recommendedBatteryAh = voltage > 0 ? (recommendedBatteryKWh * 1000) / voltage : 0;
    const qty100Ah = recommendedBatteryAh > 0 ? Math.ceil(recommendedBatteryAh / 100) : 0;
    const qty270Ah = recommendedBatteryAh > 0 ? Math.ceil(recommendedBatteryAh / 270) : 0;
    return {
      totalWh: Math.round(totalKWh * 1000),
      totalKWh,
      qty100Ah,
      recommendedBatteryAh,
      qty270Ah,
      recommendedBatteryKWh,
    };
  }

  /**
   * @returns {Array<Record<string, unknown>>}
   */
  #buildRecommendedProducts() {
    const shopDomain = this.dataset.shopDomain || '';
    const cards = this.querySelectorAll('.calculator__product-card');

    //Make sure every URL we send is fully-qualified `https://`.
    /** @param {string} url */
    const ensureAbsoluteUrl = (url) => {
      return new URL(url ?? '', location.origin)?.href || '';
    };

    /** @type {Array<Record<string, unknown>>} */
    const products = [];
    for (const card of cards) {
      const el = /** @type {HTMLElement} */ (card);
      // Skip cards the filter hid — Klaviyo should only see the products the
      // user is actually being recommended.
      if (el.classList.contains('tw:hidden')) continue;
      const variantId = el.dataset.variantId || '';
      const cartQty = Number(el.dataset.quantity) || 1;
      const imageUrl = ensureAbsoluteUrl(el.dataset.productImage || '');
      products.push({
        sku: el.dataset.productSku || '',
        imageUrl,
        thumbnail: imageUrl,
        id: variantId ? `gid://shopify/ProductVariant/${variantId}` : '',
        addToCartUrl: shopDomain && variantId ? `https://${shopDomain}/cart/${variantId}:${cartQty}` : '',
        price: el.dataset.productPrice || '',
        productUrl: ensureAbsoluteUrl(el.dataset.productUrl || ''),
        cartQty,
        name: el.dataset.productTitle || '',
      });
    }
    return products;
  }

  /**
   * Fire the "Submitted Battery Calculator" event via Klaviyo's Create Client
   * Event endpoint (https://developers.klaviyo.com/en/reference/create_client_event).
   *
   * @param {{ email: string | null, firstName: string | null, lastName: string | null, phone: string | null }} contact
   * @param {Record<string, unknown>} properties
   * @param {string} companyId
   */
  async #sendKlaviyoEvent(contact, properties, companyId) {
    /** @type {Record<string, unknown>} */
    const profileAttributes = {};
    if (contact.email) profileAttributes.email = contact.email;
    if (contact.firstName) profileAttributes.first_name = contact.firstName;
    if (contact.lastName) profileAttributes.last_name = contact.lastName;
    if (contact.phone) profileAttributes.phone_number = contact.phone;

    const body = {
      data: {
        type: 'event',
        attributes: {
          properties,
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'Submitted Battery Calculator' },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
      },
    };

    console.log('[battery-calculator] Klaviyo event payload:', body);

    try {
      const response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          revision: this.#KLAVIYO_API_REVISION,
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify(body),
      });

      if (response.status === 202) {
        console.log('[battery-calculator] Klaviyo event accepted (202)');
      } else {
        console.warn('[battery-calculator] Klaviyo event unexpected status:', response.status);
      }
    } catch (err) {
      console.error('[battery-calculator] Klaviyo event failed:', err);
    }
  }

  /**
   * @param {string} email
   * @param {string} listId
   * @param {string} companyId
   */
  async #subscribeToKlaviyoList(email, listId, companyId) {
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    console.log('[battery-calculator] Klaviyo subscription payload:', body);

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: this.#KLAVIYO_API_REVISION,
          },
          body: JSON.stringify(body),
        },
      );

      if (response.status === 202) {
        console.log('[battery-calculator] Klaviyo subscription accepted');
      } else {
        console.warn('[battery-calculator] Klaviyo subscription unexpected status:', response.status);
      }
    } catch (err) {
      console.error('[battery-calculator] Klaviyo subscription failed:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // localStorage persistence
  // ---------------------------------------------------------------------------

  #saveIfReady() {
    if (this.#contactInfoCompleted) {
      this.#saveToStorage();
    }
  }

  #saveToStorage() {
    const form = this.refs.form;
    if (!form) return;

    const formData = new FormData(form);
    /** @type {Record<string, string>} */
    const fields = {};

    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string') {
        fields[key] = value;
      }
    }

    // Capture which rows are toggled on
    /** @type {string[]} */
    const activeRows = [];
    const rows = this.refs.rows;
    if (Array.isArray(rows)) {
      for (const row of rows) {
        const toggle = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-row-toggle]'));
        if (!toggle?.checked) continue;

        const qtyEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_qty"]'));
        const rowName = qtyEl?.name?.replace('_qty', '');
        if (rowName) activeRows.push(rowName);
      }
    }

    const data = {
      currentStep: this.#activeStepIndex + 1,
      contactInfoCompleted: this.#contactInfoCompleted,
      fields,
      activeRows,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Storage full or unavailable — fail silently
    }
  }

  #loadFromStorage() {
    /** @type {string | null} */
    let raw;

    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }

    if (!raw) return;

    try {
      const data = JSON.parse(raw);

      // Check if data is expired (48 hours = 48 * 60 * 60 * 1000 = 172,800,000ms)
      if (data.timestamp) {
        const EXPIRATION_MS = 48 * 60 * 60 * 1000;
        if (Date.now() - data.timestamp > EXPIRATION_MS) {
          localStorage.removeItem(STORAGE_KEY);
          return;
        }
      }

      if (!data.contactInfoCompleted) return;

      this.#contactInfoCompleted = true;
      // Note: We intentionally do NOT reset activeStepIndex here.
      // The step should come from URL (if present) or default to step 1.
      // URL step was already set in connectedCallback() before this method was called.

      // Restore field values
      const form = this.refs.form;
      if (!form || !data.fields) return;

      for (const [name, value] of Object.entries(data.fields)) {
        const elements = form.elements.namedItem(/** @type {string} */ (name));
        if (!elements) continue;

        if (elements instanceof RadioNodeList) {
          elements.value = /** @type {string} */ (value);
        } else if (elements instanceof HTMLInputElement) {
          if (elements.type === 'checkbox') {
            // Check if checked (value is non-empty and not explicitly 'false')
            elements.checked = value && value !== 'false';
          } else {
            elements.value = /** @type {string} */ (value);
          }
        } else if (elements instanceof HTMLSelectElement) {
          elements.value = /** @type {string} */ (value);
        }
      }

      // Restore active table rows
      const rows = this.refs.rows;
      if (data.activeRows?.length && Array.isArray(rows)) {
        for (const row of rows) {
          const qtyEl = /** @type {HTMLInputElement | null} */ (row.querySelector('[name$="_qty"]'));
          if (!qtyEl) continue;

          const rowName = qtyEl.name.replace('_qty', '');
          if (!data.activeRows.includes(rowName)) continue;

          const toggle = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-row-toggle]'));
          if (toggle && !toggle.checked) {
            toggle.checked = true;
            toggle.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    } catch {
      // Corrupt data — ignore
    }
  }
}

customElements.define('battery-calculator', BatteryCalculator);
